import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from '../components/layout/PublicHeader';
import {
  ArrowLeft,
  CreditCard,
  Layout,
  Users,
  Mail,
  BarChart3,
  Sparkles,
} from 'lucide-react';

/* ══════════════════════════════════════════════
   LANDING VARIANT C — "Bold Gradient Glass"
   רקע גרדיאנט חי מונפש, כרטיסי זכוכית (glassmorphism),
   פרלקסת עכבר על המוקאפ וכותרת בגרדיאנט נושם.
   ══════════════════════════════════════════════ */

function useReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.unobserve(el);
        }
      },
      { threshold, rootMargin: '9999px 0px 0px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

/* Mouse-parallax: מזיז את המוקאפ מעט לפי מיקום העכבר */
function useParallax(strength = 12) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      el.style.transform = `perspective(1400px) rotateY(${-x * 4}deg) rotateX(${y * 4}deg) translate3d(${-x * strength}px, ${-y * strength}px, 0)`;
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, [strength]);
  return ref;
}

const FEATURES = [
  { icon: CreditCard, title: 'תרומות וסליקה', desc: 'סליקה מאובטחת, הוראות קבע וקבלות אוטומטיות.' },
  { icon: Layout, title: 'דפי גיוס', desc: 'דף תרומה מרהיב בגרירה ושחרור, עם מעקב המרות.' },
  { icon: Users, title: 'CRM ומתנדבים', desc: 'אנשי קשר, היסטוריה וסגמנטציה במקום אחד.' },
  { icon: Mail, title: 'דיוור והודעות', desc: 'מייל, SMS ו-WhatsApp עם תזמון אוטומטי.' },
  { icon: BarChart3, title: 'דוחות חיים', desc: 'דשבורד אנליטי, תחזיות וייצוא לאקסל.' },
  { icon: Sparkles, title: 'AI מובנה', desc: 'כותב תוכן, מנתח נתונים ומציע אסטרטגיות.' },
];

const LandingVariantC: React.FC = () => {
  const features = useReveal();
  const cta = useReveal();
  const parallaxRef = useParallax();

  return (
    <div dir="rtl" className="min-h-screen bg-[#080B1A] text-white font-heebo overflow-x-hidden">
      <PublicHeader />

      <style>{`
        @keyframes cGradient { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
        @keyframes cFloat { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-16px) rotate(2deg); } }
        @keyframes cPulse { 0%,100% { opacity: 0.5; transform: scale(1); } 50% { opacity: 0.9; transform: scale(1.15); } }
        @keyframes cFadeUp { from { opacity: 0; transform: translateY(28px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes cShine { from { transform: translateX(-150%) skewX(-20deg); } to { transform: translateX(250%) skewX(-20deg); } }
        .c-fade-up { animation: cFadeUp 0.9s ease-out both; }
        .c-d1 { animation-delay: 130ms; } .c-d2 { animation-delay: 260ms; } .c-d3 { animation-delay: 390ms; }
        .c-gradient-text {
          background: linear-gradient(90deg, #60a5fa, #a78bfa, #f472b6, #fbbf24, #60a5fa);
          background-size: 300% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          animation: cGradient 8s ease infinite;
        }
        .c-glass { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px); }
        .c-shine { position: relative; overflow: hidden; }
        .c-shine::after {
          content: ''; position: absolute; top: 0; bottom: 0; width: 40%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent);
          animation: cShine 3.5s ease-in-out infinite;
        }
      `}</style>

      {/* ━━━━ HERO ━━━━ */}
      <section className="relative pt-24 pb-20 px-6 overflow-hidden">
        {/* Animated gradient wash */}
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{
            background: 'linear-gradient(115deg, #1d4ed8 0%, #7c3aed 30%, #db2777 60%, #1d4ed8 100%)',
            backgroundSize: '300% 300%',
            animation: 'cGradient 14s ease infinite',
            maskImage: 'radial-gradient(ellipse 100% 80% at 50% 0%, black 20%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse 100% 80% at 50% 0%, black 20%, transparent 75%)',
          }}
        />
        {/* Floating orbs */}
        <div className="absolute top-24 right-[12%] w-4 h-4 rounded-full bg-cyan-400/70 blur-[2px] pointer-events-none" style={{ animation: 'cPulse 4s ease-in-out infinite' }} />
        <div className="absolute top-48 left-[18%] w-3 h-3 rounded-full bg-fuchsia-400/70 blur-[2px] pointer-events-none" style={{ animation: 'cPulse 5s ease-in-out infinite', animationDelay: '1s' }} />
        <div className="absolute bottom-40 right-[30%] w-2.5 h-2.5 rounded-full bg-amber-300/70 blur-[1px] pointer-events-none" style={{ animation: 'cPulse 6s ease-in-out infinite', animationDelay: '2s' }} />

        <div className="relative max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-14">
            {/* Text */}
            <div className="flex-1 text-right order-1 lg:order-2">
              <div className="c-fade-up inline-flex items-center gap-2 px-4 py-1.5 mb-6 c-glass rounded-full">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-white/85 text-sm font-semibold">חדש: עוזר AI שכותב לכם את הקמפיין</span>
              </div>

              <h1 className="c-fade-up c-d1 text-4xl sm:text-5xl md:text-6xl font-black leading-[1.12] tracking-tight mb-6">
                העמותה שלכם.
                <br />
                <span className="c-gradient-text">ברמה אחרת.</span>
              </h1>

              <p className="c-fade-up c-d2 text-lg text-white/60 leading-relaxed mb-8 max-w-lg">
                תרומות, קמפיינים, CRM, דיוור ובינה מלאכותית — פלטפורמה אחת עוצמתית שנבנתה לעמותות ישראליות.
              </p>

              <div className="c-fade-up c-d2 flex flex-col sm:flex-row gap-3 mb-8">
                <Link
                  to="/register"
                  className="c-shine inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-l from-fuchsia-500 via-violet-500 to-blue-500 text-white text-base font-bold rounded-2xl shadow-[0_10px_40px_rgba(139,92,246,0.45)] hover:shadow-[0_14px_50px_rgba(139,92,246,0.6)] transition-all duration-300 hover:scale-[1.03]"
                >
                  פתח חשבון בחינם
                  <ArrowLeft size={18} />
                </Link>
                <a
                  href="#features-c"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 c-glass text-white text-base font-semibold rounded-2xl hover:bg-white/10 transition-all duration-300"
                >
                  גלה עוד
                </a>
              </div>

              <div className="c-fade-up c-d3 flex flex-wrap gap-3">
                {['🇮🇱 מיוצר בישראל', '✅ ביטול בכל עת', '🔒 PCI מאובטח'].map(t => (
                  <span key={t} className="inline-flex items-center gap-1.5 px-3 py-1.5 c-glass rounded-full text-white/70 text-xs font-medium">{t}</span>
                ))}
              </div>
            </div>

            {/* Mockup with mouse parallax */}
            <div className="flex-1 order-2 lg:order-1 w-full c-fade-up c-d3">
              <div ref={parallaxRef} style={{ transition: 'transform 0.25s ease-out', willChange: 'transform' }}>
                <div className="relative">
                  {/* Glow behind */}
                  <div className="absolute -inset-6 bg-gradient-to-l from-fuchsia-600/40 via-violet-600/40 to-blue-600/40 blur-[50px] rounded-full pointer-events-none" />
                  {/* Browser frame */}
                  <div className="relative rounded-2xl overflow-hidden c-glass shadow-[0_50px_100px_-20px_rgba(0,0,0,0.7)]">
                    <div dir="ltr" className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.04] border-b border-white/10">
                      <div className="flex gap-1.5">
                        <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                        <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
                        <span className="w-3 h-3 rounded-full bg-[#28c840]" />
                      </div>
                      <div className="flex-1 flex justify-center">
                        <div className="w-full max-w-[200px] rounded-md bg-white/10 text-white/50 text-[11px] text-center py-1 font-medium">app.amutech.co.il</div>
                      </div>
                      <div className="w-10" />
                    </div>
                    <img src="/screenshots/income-hero.png" alt="מסך הכנסות ותרומות — עמוטק" className="w-full h-auto block bg-[#f1f5f9]" />
                  </div>
                  {/* Floating glass KPI */}
                  <div className="absolute -top-6 -left-4 z-20 flex items-center gap-2.5 px-4 py-2.5 rounded-2xl c-glass shadow-xl" style={{ animation: 'cFloat 5s ease-in-out infinite' }}>
                    <div className="w-8 h-8 rounded-full bg-emerald-400/20 flex items-center justify-center text-base">💚</div>
                    <div>
                      <p className="text-[11px] font-bold text-white leading-tight">תרומה חדשה</p>
                      <p className="text-[12px] text-emerald-300 font-black leading-tight">₪850</p>
                    </div>
                  </div>
                  <div className="absolute -bottom-5 right-6 z-20 flex items-center gap-2.5 px-4 py-2.5 rounded-2xl c-glass shadow-xl" style={{ animation: 'cFloat 6s ease-in-out infinite', animationDelay: '1.4s' }}>
                    <div className="w-8 h-8 rounded-full bg-amber-400/20 flex items-center justify-center text-base">🏆</div>
                    <div>
                      <p className="text-[11px] font-bold text-white leading-tight">יעד קמפיין</p>
                      <p className="text-[12px] text-amber-300 font-black leading-tight">84% הושג</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━━ FEATURES — glass grid ━━━━ */}
      <section id="features-c" ref={features.ref} className="relative py-24 px-6">
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-violet-600/15 rounded-full blur-[130px] pointer-events-none" />
        <div className="relative max-w-6xl mx-auto">
          <div className={`text-center mb-14 transition-all duration-700 ${features.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight mb-4">
              כל הכלים. <span className="c-gradient-text">בממשק אחד.</span>
            </h2>
            <p className="text-lg text-white/50 max-w-xl mx-auto">בנוי לעמותות שרוצות לצמוח.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className={`group p-7 rounded-3xl c-glass hover:bg-white/[0.09] hover:-translate-y-1.5 transition-all duration-500 ${features.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${i * 90}ms` }}
                >
                  <div className="w-13 h-13 w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500/30 to-violet-500/30 border border-white/10 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    <Icon size={25} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{f.title}</h3>
                  <p className="text-white/50 leading-relaxed text-[15px]">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ━━━━ CTA ━━━━ */}
      <section ref={cta.ref} className="relative px-6 pb-24">
        <div className={`relative max-w-4xl mx-auto text-center transition-all duration-700 ${cta.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="c-glass rounded-[32px] px-8 py-16 overflow-hidden relative">
            <div
              className="absolute inset-0 opacity-25 pointer-events-none"
              style={{
                background: 'linear-gradient(115deg, #1d4ed8, #7c3aed, #db2777, #1d4ed8)',
                backgroundSize: '300% 300%',
                animation: 'cGradient 10s ease infinite',
              }}
            />
            <h2 className="relative text-3xl sm:text-4xl font-black tracking-tight mb-4">
              מוכנים <span className="c-gradient-text">לרמה הבאה?</span>
            </h2>
            <p className="relative text-white/60 text-lg mb-8">7 ימי ניסיון חינם · ללא כרטיס אשראי · ביטול בכל עת</p>
            <Link
              to="/register"
              className="c-shine relative inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-l from-fuchsia-500 via-violet-500 to-blue-500 text-white text-lg font-bold rounded-2xl shadow-[0_10px_40px_rgba(139,92,246,0.5)] hover:scale-[1.04] transition-all duration-300"
            >
              פתח חשבון בחינם
              <ArrowLeft size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Mini footer ── */}
      <footer className="py-8 px-6 border-t border-white/10">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-white/40">
          <p>&copy; 2026 עמוטק – פתרונות דיגיטליים לעמותות בע"מ</p>
          <div className="flex gap-4">
            <Link to="/terms" className="hover:text-white transition-colors">תנאי שימוש</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">מדיניות פרטיות</Link>
            <Link to="/accessibility" className="hover:text-white transition-colors">נגישות</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingVariantC;
